import React, { useState, useEffect } from "react";
import { sbSetSetting, sbGetSetting } from "../utils/supabaseClient";
import TablerIconPicker from "./TablerIconPicker";
import { useToast } from "./Toast";
import "../styles/MetadataForm.css";

const MetadataForm = ({ content, sections, onSave, onCancel }) => {
  // Support both old sections and new content parameter
  const lessonContent = content || sections;
  const { success, error: showError } = useToast();
  const [title, setTitle] = useState("");
  const [duration, setDuration] = useState("5 min");
  const [tag, setTag] = useState("");
  const [icon, setIcon] = useState("book");
  const [subtitle, setSubtitle] = useState("");
  const [scheduled, setScheduled] = useState("");
  const [saving, setSaving] = useState(false);
  const [existingTags, setExistingTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState("");
  const [isAddingNewTag, setIsAddingNewTag] = useState(false);

  useEffect(() => {
    loadExistingTags();
  }, []);

  useEffect(() => {
    if (lessonContent) {
      setTitle(lessonContent.title || "");
      setDuration(lessonContent.dur || "5 min");
      setTag(lessonContent.tag || "");
      setSelectedTag(lessonContent.tag || "");
      setIcon(lessonContent.icon || "book");
      setSubtitle(lessonContent.subtitle || "");
      setScheduled(lessonContent.scheduled ? lessonContent.scheduled.split('T')[0] : "");
      setIsAddingNewTag(false);
    }
  }, [lessonContent]);

  const loadExistingTags = async () => {
    try {
      const lessons = await sbGetSetting("origamic-lessons");
      if (Array.isArray(lessons)) {
        const tags = [...new Set(lessons.map(l => l.tag).filter(Boolean))].sort();
        setExistingTags(tags);
      }
    } catch (err) {
      console.error("Error loading tags:", err);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      showError("Please enter a lesson title");
      return;
    }

    setSaving(true);

    try {
      // Create the lesson object (as draft - only publish sends notifications)
      const newLesson = {
        id: `lesson_${Date.now()}`,
        dur: duration,
        tag: tag,
        body: lessonContent,
        icon: icon,
        title: title.trim(),
        addedAt: new Date().toISOString(),
        subtitle: subtitle.trim(),
        scheduled: scheduled ? new Date(scheduled).toISOString() : null,
        draft: true, // Save as draft - must be published to send notifications
      };

      // Get existing lessons from app_settings
      let lessons = await sbGetSetting("origamic-lessons");
      if (!Array.isArray(lessons)) {
        lessons = [];
      }

      // Add new lesson
      lessons.push(newLesson);

      // Save back to app_settings
      await sbSetSetting("origamic-lessons", lessons);

      // Notify parent
      success("Lesson saved");
      onSave(newLesson);
    } catch (err) {
      console.error("Error saving lesson:", err);
      showError("Failed to save lesson");
    }

    setSaving(false);
  };

  return (
    <div className="metadata-overlay">
      <div className="metadata-modal">
        <h2>Lesson Metadata</h2>
        <p className="metadata-subtitle">Add details before saving</p>

        <form onSubmit={handleSave}>
          {/* Title */}
          <div className="form-group">
            <label>Lesson Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Introduction to AI"
              disabled={saving}
            />
          </div>

          {/* Icon with Tabler Icon Picker */}
          <div className="form-group">
            <label>Icon</label>
            <TablerIconPicker
              value={icon}
              onChange={(value) => setIcon(value)}
            />
          </div>

          {/* Duration */}
          <div className="form-group">
            <label>Duration</label>
            <input
              type="text"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="5 min"
              disabled={saving}
            />
          </div>

          {/* Tag */}
          <div className="form-group">
            <label>Tag/Category</label>
            {!isAddingNewTag ? (
              <select
                value={selectedTag}
                onChange={(e) => {
                  if (e.target.value === "new") {
                    setIsAddingNewTag(true);
                    setSelectedTag("");
                    setTag("");
                  } else {
                    setSelectedTag(e.target.value);
                    setTag(e.target.value);
                  }
                }}
                disabled={saving}
              >
                <option value="">-- Select Category --</option>
                {existingTags.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
                <option value="new">+ Add New Category</option>
              </select>
            ) : (
              <>
                <input
                  type="text"
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  placeholder="Enter new category name..."
                  autoFocus
                  disabled={saving}
                />
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingNewTag(false);
                    setTag("");
                  }}
                  disabled={saving}
                  style={{
                    marginTop: "8px",
                    padding: "8px 12px",
                    background: "#7A9CC4",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "12px",
                  }}
                >
                  Back to Categories
                </button>
              </>
            )}
          </div>

          {/* Subtitle */}
          <div className="form-group">
            <label>Subtitle/Description</label>
            <textarea
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="Brief description of the lesson"
              rows="3"
              disabled={saving}
            />
          </div>

          {/* Scheduled Date */}
          <div className="form-group">
            <label>Scheduled Date (Optional)</label>
            <input
              type="date"
              value={scheduled}
              onChange={(e) => setScheduled(e.target.value)}
              disabled={saving}
              title="Schedule when this item should be published"
              style={{ width: "100%" }}
            />
            <small style={{ color: "#7A9CC4", marginTop: "4px", display: "block" }}>
              Optional: Schedule this item to be published on a future date
            </small>
          </div>

          {/* Actions */}
          <div className="form-actions">
            <button
              type="submit"
              className="primary"
              disabled={saving || !title.trim()}
            >
              {saving ? "Saving..." : "Save Lesson"}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MetadataForm;
